import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Search, Users, AlertTriangle, CreditCard, Loader2, Banknote, Smartphone } from 'lucide-react';

const CustomersPage = () => {
  const { api, isOwner, permissions } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('customers');

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    credit_limit: '',
    notes: '',
  });

  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    notes: '',
    keep_excess_as_wallet: false,
  });

  const fetchData = useCallback(async () => {
    try {
      const [customersRes, debtsRes] = await Promise.all([
        api.get('/customers', { params: { include_inactive: showInactive } }),
        api.get('/debts').catch(() => ({ data: [] })),
      ]);
      setCustomers(customersRes.data);
      setDebts(debtsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api, showInactive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
      credit_limit: '',
      notes: '',
    });
    setSelectedCustomer(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name,
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      credit_limit: customer.credit_limit.toString(),
      notes: customer.notes || '',
    });
    setDialogOpen(true);
  };

  const openPaymentDialog = (customer) => {
    setSelectedCustomer(customer);
    setPaymentData({
      amount: customer.current_debt.toString(),
      payment_method: 'cash',
      notes: '',
      keep_excess_as_wallet: false,
    });
    setPaymentDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Please enter customer name');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        credit_limit: parseFloat(formData.credit_limit) || 0,
      };

      if (selectedCustomer && dialogOpen) {
        await api.put(`/customers/${selectedCustomer.id}`, payload);
        toast.success('Customer updated successfully');
      } else {
        await api.post('/customers', payload);
        toast.success('Customer added successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handlePayment = async () => {
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      await api.post('/debts/payment', {
        customer_id: selectedCustomer.id,
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        notes: paymentData.notes,
        keep_excess_as_wallet: paymentData.keep_excess_as_wallet,
      });
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      setSelectedCustomer(null);
      fetchData();
    } catch (error) {
      if (
        error.response?.data?.detail ===
        'Payment exceeds outstanding debt. Confirm keeping excess on wallet to proceed.'
      ) {
        setPaymentData((prev) => ({ ...prev, keep_excess_as_wallet: true }));
      }
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Delete this customer (soft delete)?')) return;
    setSaving(true);
    try {
      await api.delete(`/customers/${customerId}`);
      toast.success('Customer deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreCustomer = async (customerId) => {
    setSaving(true);
    try {
      await api.post(`/customers/${customerId}/restore`);
      toast.success('Customer restored');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore customer');
    } finally {
      setSaving(false);
    }
  };

  const filteredCustomers = customers.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.phone && c.phone.includes(searchQuery))
  );

  const formatCurrency = (amount) => {
    return `UGX ${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const getAgingBadge = (aging) => {
    switch (aging) {
      case '0-7':
        return <Badge variant="default">Current</Badge>;
      case '8-30':
        return <Badge variant="warning" className="bg-warning text-warning-foreground">Overdue</Badge>;
      case '30+':
        return <Badge variant="destructive">Critical</Badge>;
      default:
        return <Badge variant="secondary">{aging}</Badge>;
    }
  };

  const canManageCustomers = isOwner || permissions?.manage_customers !== false;
  const canDelete = isOwner || permissions?.delete_customers === true;
  const paymentAmount = parseFloat(paymentData.amount || '0') || 0;
  const selectedOutstanding = parseFloat(selectedCustomer?.current_debt || 0) || 0;
  const wouldExceedDebt = paymentAmount > selectedOutstanding;
  const projectedExcess = Math.max(0, paymentAmount - selectedOutstanding);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalDebt = customers.reduce((sum, c) => sum + c.current_debt, 0);
  const customersWithDebt = customers.filter((c) => c.current_debt > 0).length;

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Customers & Debts</h1>
          <p className="text-muted-foreground">
            {customers.length} customers • {customersWithDebt} with outstanding debt
          </p>
        </div>
        {canManageCustomers && (
          <Button onClick={openAddDialog} className="neon-green" data-testid="add-customer-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add Customer
          </Button>
        )}
        <Button
          type="button"
          variant={showInactive ? 'default' : 'outline'}
          onClick={() => setShowInactive((v) => !v)}
        >
          {showInactive ? 'Hiding Archived' : 'Show Archived'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="shadow-sm border-border/60 bg-card/95">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Customers</p>
                <p className="text-3xl font-bold">{customers.length}</p>
              </div>
              <Users className="h-10 w-10 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className={totalDebt > 0 ? 'debt-glow border-destructive/30 shadow-lg' : 'shadow-sm border-border/60 bg-card/95'}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Outstanding</p>
                <p className={`text-3xl font-bold font-mono ${totalDebt > 0 ? 'text-destructive' : ''}`}>
                  {formatCurrency(totalDebt)}
                </p>
              </div>
              <CreditCard className="h-10 w-10 text-destructive" />
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-border/60 bg-card/95">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Customers with Debt</p>
                <p className="text-3xl font-bold">{customersWithDebt}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-warning" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="customers" data-testid="customers-tab">Customers</TabsTrigger>
          <TabsTrigger value="debts" data-testid="debts-tab">Outstanding Debts</TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="mt-4">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12"
                data-testid="customers-search"
              />
            </div>
          </div>

          {/* Customers Table */}
          <Card className="shadow-sm border-border/60 bg-card/95">
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-450px)]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Credit Limit</TableHead>
                        <TableHead>Current Debt</TableHead>
                      <TableHead>Wallet</TableHead>
                      <TableHead>Available</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.map((customer) => (
                      <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{customer.name}</p>
                            {customer.email && (
                              <p className="text-xs text-muted-foreground">{customer.email}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{customer.phone || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {formatCurrency(customer.credit_limit)}
                        </TableCell>
                        <TableCell>
                          <span className={`font-mono text-sm ${customer.current_debt > 0 ? 'text-destructive font-semibold' : ''}`}>
                            {formatCurrency(customer.current_debt)}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-sm text-secondary">
                          {formatCurrency(customer.wallet_balance || 0)}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-primary">
                          {formatCurrency(customer.credit_limit - customer.current_debt)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={customer.is_active ? 'default' : 'secondary'}>
                            {customer.is_active ? 'Active' : 'Archived'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          {customer.current_debt > 0 && customer.is_active && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openPaymentDialog(customer)}
                              data-testid={`pay-debt-${customer.id}`}
                            >
                              Record Payment
                            </Button>
                          )}
                          {canManageCustomers && customer.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(customer)}
                              data-testid={`edit-customer-${customer.id}`}
                            >
                              Edit
                            </Button>
                          )}
                          {canDelete && customer.is_active && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteCustomer(customer.id)}
                            >
                              Delete
                            </Button>
                          )}
                          {canDelete && !customer.is_active && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRestoreCustomer(customer.id)}
                            >
                              Restore
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredCustomers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12">
                          <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <p className="text-muted-foreground">No customers found</p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="debts" className="mt-4">
          <Card className="shadow-sm border-border/60 bg-card/95">
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-400px)]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Receipt</TableHead>
                      <TableHead>Original</TableHead>
                      <TableHead>Paid</TableHead>
                      <TableHead>Remaining</TableHead>
                      <TableHead>Age</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {debts.map((debt) => (
                      <TableRow key={debt.id} data-testid={`debt-row-${debt.id}`}>
                        <TableCell className="font-medium">{debt.customer_name}</TableCell>
                        <TableCell className="font-mono text-xs">{debt.receipt_number}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {formatCurrency(debt.original_amount)}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-primary">
                          {formatCurrency(debt.paid_amount)}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-destructive font-semibold">
                          {formatCurrency(debt.remaining_amount)}
                        </TableCell>
                        <TableCell>{debt.days_overdue} days</TableCell>
                        <TableCell>{getAgingBadge(debt.aging_category)}</TableCell>
                      </TableRow>
                    ))}
                    {debts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12">
                          <CreditCard className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <p className="text-muted-foreground">No outstanding debts</p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {selectedCustomer ? 'Edit Customer' : 'Add New Customer'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Customer Name *</Label>
              <Input
                placeholder="e.g., John Okello"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                data-testid="customer-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  placeholder="e.g., 0772123456"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  data-testid="customer-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="customer@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  data-testid="customer-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Credit Limit (UGX)</Label>
              <Input
                type="number"
                placeholder="e.g., 100000"
                value={formData.credit_limit}
                onChange={(e) => handleChange('credit_limit', e.target.value)}
                className="font-mono"
                data-testid="customer-credit-limit-input"
              />
              <p className="text-xs text-muted-foreground">
                Maximum amount this customer can owe before being blocked from credit sales
              </p>
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                placeholder="e.g., Kampala, Nakawa"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                data-testid="customer-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                placeholder="Any additional notes..."
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                data-testid="customer-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving} className="neon-green" data-testid="save-customer-btn">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                selectedCustomer ? 'Update Customer' : 'Add Customer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Record Debt Payment</DialogTitle>
            <DialogDescription>
              {selectedCustomer && (
                <>
                  Customer: <strong>{selectedCustomer.name}</strong>
                  <br />
                  Outstanding: <strong className="text-destructive">{formatCurrency(selectedCustomer.current_debt)}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Amount (UGX) *</Label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                className="h-14 text-2xl font-mono text-center"
                data-testid="payment-amount-input"
              />
              {wouldExceedDebt && (
                <p className="text-xs text-warning">
                  This payment exceeds outstanding debt by <strong>{formatCurrency(projectedExcess)}</strong>.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Payment Method</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant={paymentData.payment_method === 'cash' ? 'default' : 'outline'}
                  onClick={() => setPaymentData({ ...paymentData, payment_method: 'cash' })}
                  className="h-12"
                  data-testid="payment-method-cash"
                >
                  <Banknote className="h-4 w-4 mr-2" />
                  Cash
                </Button>
                <Button
                  type="button"
                  variant={paymentData.payment_method === 'mtn_mobile_money' ? 'default' : 'outline'}
                  onClick={() => setPaymentData({ ...paymentData, payment_method: 'mtn_mobile_money' })}
                  className="h-12"
                  data-testid="payment-method-mtn"
                >
                  <Smartphone className="h-4 w-4 mr-2" />
                  MTN
                </Button>
                <Button
                  type="button"
                  variant={paymentData.payment_method === 'airtel_money' ? 'default' : 'outline'}
                  onClick={() => setPaymentData({ ...paymentData, payment_method: 'airtel_money' })}
                  className="h-12"
                  data-testid="payment-method-airtel"
                >
                  <Smartphone className="h-4 w-4 mr-2" />
                  Airtel
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                placeholder="e.g., Partial payment"
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                data-testid="payment-notes-input"
              />
            </div>

            <div className="rounded-lg border border-border/60 p-3 bg-background/70 shadow-sm">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="keep-excess-wallet"
                  checked={paymentData.keep_excess_as_wallet}
                  onCheckedChange={(checked) =>
                    setPaymentData({ ...paymentData, keep_excess_as_wallet: Boolean(checked) })
                  }
                />
                <div className="space-y-1">
                  <Label htmlFor="keep-excess-wallet" className="cursor-pointer">
                    Keep excess payment in customer wallet
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    If amount is above outstanding debt, excess is credited to wallet for future purchases.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePayment} disabled={saving} className="neon-green" data-testid="confirm-debt-payment-btn">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Recording...
                </>
              ) : (
                'Record Payment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
